import React, { useState, useEffect } from 'react';
import { post, get, del } from 'aws-amplify/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../utils/dateUtils';
import './Announcements.css';

const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [message, setMessage] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const { getAuthToken } = useAuth();

  // Fetch announcements on component mount
  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoadingList(true);
      setError(null);

      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: '/v1/announcements',
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setAnnouncements(data.announcements || []);
    } catch (err) {
      console.error('Error fetching announcements:', err);
      setError('Failed to fetch announcements');
    } finally {
      setLoadingList(false);
    }
  };

  const createAnnouncement = async (e) => {
    e.preventDefault();

    if (!message.trim()) {
      setError('Please enter an announcement message');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = post({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/announcements',
        options: {
          headers: {
            Authorization: token,
          },
          body: {
            message: message.trim(),
            expires_in_days: expiresInDays,
          },
        },
      });

      const response = await restOperation.response;
      await response.body.json();

      setSuccessMessage('Announcement created successfully!');
      setMessage('');
      setExpiresInDays(30);

      // Refresh the announcements list
      await fetchAnnouncements();
    } catch (err) {
      console.error('Error creating announcement:', err);
      setError(err.message || 'Failed to create announcement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const deleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) {
      return;
    }

    try {
      setDeletingId(announcementId);
      setError(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = del({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/announcements/${announcementId}`,
        options: {
          headers: {
            Authorization: token,
          },
        },
      });

      await restOperation.response;

      setSuccessMessage('Announcement deleted successfully!');

      // Refresh the announcements list
      await fetchAnnouncements();
    } catch (err) {
      console.error('Error deleting announcement:', err);
      setError(err.message || 'Failed to delete announcement. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="announcements-container">
      <h1>App Announcements</h1>
      <p className="page-description">
        Create and manage announcements that will be displayed in the mobile app's scrolling banner.
      </p>

      {/* Create Announcement Form */}
      <div className="announcement-form-section">
        <h2>Create New Announcement</h2>
        <form onSubmit={createAnnouncement} className="announcement-form">
          <div className="form-group">
            <label htmlFor="message">Announcement Message</label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter your announcement message..."
              rows="4"
              disabled={loading}
              maxLength={200}
            />
            <small className="character-count">
              {message.length}/200 characters
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="expires">Expires In (Days)</label>
            <input
              type="number"
              id="expires"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(parseInt(e.target.value))}
              min="1"
              max="90"
              disabled={loading}
            />
            <small>Announcement will automatically expire after {expiresInDays} days</small>
          </div>

          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="alert alert-success">
              {successMessage}
            </div>
          )}

          <button type="submit" className="btn btn-primary" disabled={loading || !message.trim()}>
            {loading ? 'Creating...' : 'Create Announcement'}
          </button>
        </form>
      </div>

      {/* Announcements List */}
      <div className="announcements-list-section">
        <div className="list-header">
          <h2>Current Announcements</h2>
          <button onClick={fetchAnnouncements} className="btn btn-secondary" disabled={loadingList}>
            {loadingList ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {loadingList ? (
          <div className="loading-spinner">Loading announcements...</div>
        ) : announcements.length === 0 ? (
          <div className="empty-state">
            <p>No active announcements</p>
            <small>Create your first announcement above to get started</small>
          </div>
        ) : (
          <div className="announcements-grid">
            {announcements.map((announcement) => (
              <div key={announcement.announcement_id} className="announcement-card">
                <div className="announcement-header">
                  <span className={`status-badge ${announcement.status}`}>
                    {announcement.status}
                  </span>
                  <span className="announcement-date">
                    {formatDateTime(announcement.created_at)}
                  </span>
                </div>
                <div className="announcement-message">
                  {announcement.message}
                </div>
                <div className="announcement-actions">
                  <button
                    onClick={() => deleteAnnouncement(announcement.announcement_id)}
                    className="btn-delete"
                    disabled={deletingId === announcement.announcement_id}
                  >
                    {deletingId === announcement.announcement_id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Announcements;
